import React, {useEffect, useRef, useCallback, useMemo} from 'react';
import * as THREE from 'three';

import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import {useDispatch, useSelector} from "react-redux";
import {selectSiloGroupsState} from "../../../store/selectors";
import {silogroupAC} from "../../../store/branches/silo-group/actionCreators";
import {useCurrentSelection} from "../../../hooks/useCurrentSelection";
const public_url = process.env.PUBLIC_URL
const minTemp = 10;    // green
const maxTemp = 50;  // red

const getTemperatureColour = (value, unit) => {
    // Convert Fahrenheit to Celsius if necessary
    if (unit === "°F") {
        value = (value - 32) * 5 / 9; // Convert to Celsius
    }

    const clamped = Math.max(minTemp, Math.min(maxTemp, value));

    if (value <= minTemp) {
        return '#00FF00';
    }

    if (value >= maxTemp) {
        return '#FF0000';
    }

    const gradientRange = maxTemp - minTemp; // 40 degrees
    const percent = (clamped - minTemp) / gradientRange; // 0.0 to 1.0

    let r, g;
    const splitPoint = 0.5;

    if (percent <= splitPoint) {
        r = Math.floor(255 * (percent / splitPoint));
        g = 255;
    } else {
        r = 255;

        g = Math.floor(255 * (1 - (percent - splitPoint) / splitPoint));
    }

    const redHex = r.toString(16).padStart(2, '0');
    const greenHex = g.toString(16).padStart(2, '0');

    return `#${redHex}${greenHex}00`;
};

const getTextColor = (backgroundColor) => {

    const hex = backgroundColor.startsWith('#') ? backgroundColor.slice(1) : backgroundColor;

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    return brightness > 140 ? "black" : "white";
};

const SiloModelCanvas = ({wsData,onSensorClick,selectedSensorIds}) => {
    const dispatch = useDispatch();
    const sensorIdMap = useRef(new Map<string, string | number>());
    const {device} = useCurrentSelection();
    const { configData } = useSelector(selectSiloGroupsState);
    const loadIdRef = useRef(0);

   useEffect(() => {
        if (device?.id) {
             dispatch(silogroupAC.fetchSiloConfigRequest(device.id));
        }
    }, [device?.id, dispatch]);

    const mountRef = useRef(null);

    const stateRef = useRef({
        renderer: null,
        labelRenderer: null as CSS2DRenderer | null,
        scene: null,
        camera: null,
        controls: null,
        cylinderMesh: null,
        meshByName: new Map(),
        clickableBoxes: [],
        frameId: null,
        raycaster: null, // Initialized inside useEffect
        mouse: null,     // Initialized inside useEffect
        warningMeshes: new Set<THREE.Mesh>(),
        alarmMeshes: new Set<THREE.Mesh>()
    });

    // Ref to hold the shared material instance (created once)
    const newMaterial = useRef(
        new THREE.MeshStandardMaterial({
            color: new THREE.Color(0xcccccc), // Default color
            metalness: 0.1,
            roughness: 0.4,
            side: THREE.DoubleSide
        })
    );

      const positionMap = useMemo(() => {
      const defaultMapData = [
            ["1100", { meshAlias: "cable_1", name: "Channel 1" }],
            ["1200", { meshAlias: "cable_2", name: "Channel 2" }],
            ["1300", { meshAlias: "cable_3", name: "Channel 3" }],
            ["1400", { meshAlias: "cable_4", name: "Channel 4" }],
            ["1500", { meshAlias: "cable_5", name: "Channel 5" }],
            ["1600", { meshAlias: "cable_6", name: "Channel 6" }],
            ["1700", { meshAlias: "cable_7", name: "Channel 7" }],
            ["1800", { meshAlias: "cable_8", name: "Channel 8" }],
        ];

        // Start with the default map
        const map = new Map<string | number, { meshAlias: string, name: string }>(defaultMapData as any);
        const devices = configData?.configuration_data || [];

        devices.forEach(deviceConfig => {
            deviceConfig.channels.forEach(channel => {
                if (channel.isEnabled && channel.mappedPosition) {
                    map.set(channel.channelId, {
                    meshAlias: channel.mappedPosition,
                    name: channel.channelName
                //    name: channel.positionName + '-' + channel.channelName
                });
                }
            });
        });
        return map;
    }, [configData]);


// --- 1. Message/Data Processing Logic ---
    const processWsData = useCallback((data) => {
        let jsonObject;

        if (!data) return;
        if (!stateRef.current.scene) {
            return;
        }


        // Try to parse if it's a string (standard WS message format)
        if (typeof data === 'string') {
            try {
                jsonObject = JSON.parse(data);
            } catch (e) {
                console.error("Error parsing incoming WebSocket data:", e);
                return;
            }
        } else {
            // Assume it's already a parsed object
            jsonObject = data;
        }

        const { meshByName} = stateRef.current;
        const activeCableKeys = new Set<string>();
        const activeCableParentKeys = new Set<string>();
        stateRef.current.warningMeshes.clear();
        stateRef.current.alarmMeshes.clear();
        stateRef.current.scene.traverse((child) => {
        if (child.type === 'Mesh') {
            const mesh = child as THREE.Mesh;
            const meshNameLower = mesh.name.toLowerCase();

            // If the mesh is a cable  ('cable_') or a sensor point ('c1_s1'), hide it.
            if ((meshNameLower.includes('c'))) {
                 mesh.visible = false;
            }
            if (mesh.userData.label) {
                mesh.userData.label.element.style.visibility = 'hidden';
            }
        }
    });

        if (jsonObject && Array.isArray(jsonObject.groups)) {
            const tempMap = new Map<string, string | number>();
             const currentSelectedIds = new Set(selectedSensorIds);
             jsonObject.groups.forEach(cable => {

                const mappedPositionAlias = positionMap.get(cable.Id);

                if (!mappedPositionAlias) return;

                const cableParentKey = mappedPositionAlias.meshAlias;
                const meshNameForCable = cableParentKey;
                if (!meshNameForCable) return;

                // This is the actual GLB mesh root, e.g. "c1"
                const cableMeshRoot = meshNameForCable.toString().toLowerCase();
                let cableRoot = cableMeshRoot.toLowerCase();

                // Convert "cable_1" → "c1"
                if (cableRoot.startsWith("cable_")) {
                    const num = cableRoot.replace("cable_", "");

                    cableRoot = "c" + num;
                }
                // Add the cable to the active list

                if (cable.sensors != null) {
                   activeCableKeys.add(cableRoot);

                   activeCableParentKeys.add(cableParentKey);
                }

                const sensorCount = cable.sensors?.length || 0;
                const isSparse = sensorCount > 0 && sensorCount < 20;
                const siloType = configData?.silo_type?.name;
                const isFlat = siloType?.includes("Flat") ?? false;

                cable.sensors?.forEach((sensor, index) => {
                    const sensorMatch = sensor.Name.match(/\d+/);
                    const sensorNumber = sensorMatch ? parseInt(sensorMatch[0], 10) : null;

                    const meshKey = `${cableRoot}_s${sensorNumber}`.toLowerCase();
                    const mesh = meshByName.get(meshKey);

                    tempMap.set(mesh, sensor.Id);
                    // Note: Ensure the sensor object contains Value and Unit properties
                    const sensorColour = getTemperatureColour(sensor.Value, sensor.Meta.Units);

                    if (mesh) {
                         mesh.visible = true;
                       if (isSparse) {
                            if(!isFlat){
                                const cableLength =1.5;
                                const verticalShift = 0.2;
                                const newPos = (index / (sensorCount - 1)) * cableLength;
                                mesh.position.y = newPos - (cableLength / 2) + verticalShift;
                            }
                            else{
                                if (!mesh.userData.originalPos) {
                                mesh.userData.originalPos = mesh.position.clone();
                            }


                            const startY = -1.8; // The lowest point you want a sensor to go
                            const endY = -0.3;   // The highest point you want a sensor to go
                            const availableRange = endY - startY;

                            const interpolation = index / (sensorCount - 1);

                            mesh.position.y = startY + (interpolation * availableRange);
                            }


                        }
                        if (sensor.Status === "WARNING") {
                            stateRef.current.warningMeshes.add(mesh);
                        }
                        if(sensor.Alarm){
                             stateRef.current.alarmMeshes.add(mesh);
                        }
                       if (currentSelectedIds.has(sensor.Id)) {

                            if (mesh.userData.label) {
                                mesh.userData.label.element.style.visibility = 'visible';
                            }
                        }
                       mesh.material.color.set(sensorColour);
                       mesh.material.needsUpdate = true;

                      if (mesh.userData.label) {
                            // Find the sensor's label element
                            const labelDiv = mesh.userData.label.element;
                            labelDiv.textContent = sensor.Value + (sensor.Meta.Units === "C" ? 'ºC' : sensor.Meta.Units);
                            labelDiv.style.backgroundColor = sensorColour;
                            labelDiv.style.color = getTextColor(sensorColour);
                        }
                    }
                });
            });
             sensorIdMap.current = tempMap;
        }

        stateRef.current.scene.traverse((child) => {
            if (child.type === 'Mesh') {
                const mesh = child as THREE.Mesh;
                const meshNameLower = mesh.name.toLowerCase();

                // Check if the mesh name starts with any of the active cable keys (e.g., 'c1_')
                const isCableSegment = Array.from(activeCableKeys).some(key => meshNameLower.startsWith(key + '_'));

                // Also check the mesh name itself in case the entire segment is just 'c1'
                const isSensor = meshNameLower.includes('_s');
                const isCableRoot = activeCableKeys.has(meshNameLower);
                const isCableParent = activeCableParentKeys.has(meshNameLower);
                const sensorId = sensorIdMap.current.get(mesh);

                if (isCableSegment || isCableRoot ||isCableParent) {
                    if(!isSensor){
                        mesh.visible = true;
                    }

                    if (sensorId !== undefined) {
                        mesh.userData.sensorId = sensorId;
                    }

                   if (isCableParent && mesh.userData.label) {
                        mesh.userData.label.element.style.visibility = 'visible';
                    }
                }
            }
        });
    }, [device?.id]);

    useEffect(() => {
        if (wsData) {
            processWsData(wsData);
        }
    }, [wsData, processWsData]);


    useEffect(() => {
        const currentMount = mountRef.current;

        if (!currentMount) return;
        const loadId = ++loadIdRef.current;
        while (currentMount.firstChild) {
            currentMount.removeChild(currentMount.firstChild);
        }

        const state = stateRef.current;

        state.meshByName.clear();
        state.clickableBoxes = [];
        state.warningMeshes.clear();
        state.alarmMeshes.clear();

        // Initialize THREE objects
        state.raycaster = new THREE.Raycaster();
        state.mouse = new THREE.Vector2();
        state.raycaster.params.Mesh.threshold = 1;

        // 1. Scene, Camera, Renderer Setup
        const { clientWidth, clientHeight } = currentMount;
        state.scene = new THREE.Scene();
        state.camera = new THREE.PerspectiveCamera(50, clientWidth / clientHeight, 0.1, 100);
        state.camera.position.set(2, 2, 3);

        state.renderer = new THREE.WebGLRenderer({ antialias: true });
        state.renderer.setSize(clientWidth, clientHeight);
        currentMount.appendChild(state.renderer.domElement);

        state.labelRenderer = new CSS2DRenderer();
        state.labelRenderer.setSize(clientWidth, clientHeight);
        state.labelRenderer.domElement.style.position = 'absolute';
        state.labelRenderer.domElement.style.top = '0px';
        state.labelRenderer.domElement.style.left = '0px';
        state.labelRenderer.domElement.style.pointerEvents = 'none'; // Crucial! Allows mouse interaction with canvas underneath
        state.labelRenderer.domElement.style.zIndex = '10';
        currentMount.appendChild(state.labelRenderer.domElement);

        // 2. Controls and Lighting
        state.controls = new OrbitControls(state.camera, state.renderer.domElement);
        state.controls.enableDamping = true;
        state.scene.add(new THREE.AmbientLight(0xffffff, 1.2));
        const dirLight = new THREE.DirectionalLight(0xffffff, 2);
        dirLight.position.set(3, 10, 5);
        state.scene.add(dirLight);
        state.scene.background = new THREE.Color(0xf0f0f0);



    let isCancelled = false;
    let url = `${window.location.origin}${public_url}/assets/models/silo_small.glb`;
    if(configData?.silo_type?.model_url) {
        url = `${window.location.origin}${public_url}${configData?.silo_type.model_url}`;
    }

        // 3. GLTF Loading
      const loader = new GLTFLoader();

       loader.load(url , (gltf) => {
           if (isCancelled) return;
            if (loadId !== loadIdRef.current) return;
              const obj = gltf.scene;
              stateRef.current.scene.add(obj);

              const materialToApply = newMaterial.current;

              const applyInitialColor = (hex) => {
                  const color = new THREE.Color(hex);
                  materialToApply.color.copy(color); // Update shared base color

                  obj.traverse((child) => {
                      if (child.type === 'Mesh') {
                          const mesh = child as THREE.Mesh;
                          let materialInstance;

                          // Check if the mesh is a SENSOR element that needs dynamic coloring and to be clickable.
                          if (mesh.name.includes('c') && !mesh.name.includes('cable')) {

                              // 1. CLONE THE MATERIAL: This creates a NEW, independent material object
                              // that can be colored by the WebSocket data without affecting others.
                              materialInstance = materialToApply.clone();

                              stateRef.current.meshByName.set(mesh.name.toLowerCase(), mesh);

                              stateRef.current.clickableBoxes.push(mesh);
                              const labelDiv = document.createElement('div');
                              labelDiv.className = 'sensor-label'; // Use CSS for actual styling
                              labelDiv.textContent = '---'; // Initial text
                              labelDiv.style.cssText = `
                                    position: absolute;
                                    padding: 2px 6px;
                                    border-radius: 4px;
                                    font: 12px/1.2 system-ui, sans-serif;
                                    color: #fff;
                                    background: rgba(0,0,0,0.55);
                                    pointer-events: none;
                                    white-space: nowrap;
                                    visibility: hidden; /* Hide initially */
                                `;
                              const sensorLabel = new CSS2DObject(labelDiv);

                              sensorLabel.position.set(0, 0, 0);
                              mesh.add(sensorLabel);
                              mesh.userData.label = sensorLabel;

                          } else {
                              // Non-sensor meshes (main silo body) share the single newMaterial.current instance
                              materialInstance = materialToApply;
                          }
                          const cableMatch = mesh.name.match(/cable_|c\d+$/i);
                          if (cableMatch && !mesh.name.includes('sensor')) {
                              const labelDiv = document.createElement('div');
                              labelDiv.className = 'cable-label';

                              const displayName = positionMap.values().find(v => v.meshAlias.toLowerCase() === mesh.name.toLowerCase())?.name;

                              labelDiv.textContent = displayName; // Show the cable name

                              labelDiv.style.cssText = `
                            position: absolute;
                                    padding: 2px 6px;
                                    border-radius: 4px;
                                    font: 12px/1.2 system-ui, sans-serif;
                                    color: #fff;
                                    background: rgba(0,0,0,0.55);
                                    pointer-events: none;
                                    white-space: nowrap;
                            font-size: 14px;
                        `;

                              const cableLabel = new CSS2DObject(labelDiv);
                              const box = new THREE.Box3().setFromObject(mesh);
                              const height = box.max.y - box.min.y;
                              cableLabel.position.set(0, height / 2 - 160, 0);

                              mesh.add(cableLabel);
                              mesh.userData.label = cableLabel; // Use 'label' to manage visibility in Pass 3
                              mesh.userData.isCableParent = true; // Mark as a structural label
                          }

                          // Robust Material Assignment (handles single material or array of materials)
                          if (mesh.material && Array.isArray(mesh.material)) {
                              mesh.material = mesh.material.map(() => materialInstance);
                          } else {
                              mesh.material = materialInstance;
                          }

                      }
                  });
              };

              applyInitialColor("#999999");
          });

        let frameCount = 0;
        const blinkInterval = 10;
        const warningColor = new THREE.Color(0xCC0000);
         const alarmColor = new THREE.Color(0x000000);
        const originalColors = new Map<THREE.Mesh, THREE.Color>();
        // 4. Animation Loop
        const animate = () => {
            state.frameId = requestAnimationFrame(animate);
            state.controls.update();
            state.renderer.render(state.scene, state.camera);
            state.labelRenderer!.render(state.scene!, state.camera!);

            frameCount++;
            if (frameCount % blinkInterval === 0) {
                state.warningMeshes.forEach(mesh => {
                    const material = mesh.material as THREE.Material;
                    mesh.visible = !mesh.visible;

                     if (mesh.visible) {
                        // 1. Save the current color (temperature color) if not already saved
                        if (!originalColors.has(mesh)) {
                            originalColors.set(mesh, material.color.clone());
                        }
                        // 2. Set the mesh to the bright warning color
                        material.color.copy(warningColor);
                        material.needsUpdate = true;
                    } else {
                        // 3. Set the mesh back to its original (temperature) color
                        const original = originalColors.get(mesh);
                        if (original) {
                            material.color.copy(original);
                            material.needsUpdate = true;
                        }
                    }
                });
                 state.alarmMeshes.forEach(mesh => {
                    const material = mesh.material as THREE.Material;
                    mesh.visible = !mesh.visible;

                     if (mesh.userData.label) {
                       // mesh.userData.label.element.style.visibility = mesh.visible ? 'visible' : 'hidden';
                    }
                     if (mesh.visible) {
                        // 1. Save the current color (temperature color) if not already saved
                        if (!originalColors.has(mesh)) {
                            originalColors.set(mesh, material.color.clone());
                        }
                        // 2. Set the mesh to the bright warning color
                        material.color.copy(alarmColor);
                        material.needsUpdate = true;
                    } else {
                        // 3. Set the mesh back to its original (temperature) color
                        const original = originalColors.get(mesh);
                        if (original) {
                            material.color.copy(original);
                            material.needsUpdate = true;
                        }
                    }
                });
                frameCount = 0; // Reset frame count
            }
        };
        animate();

        // 5. Event Listeners (Resize and Click)
        const handleResize = () => {
            const { clientWidth, clientHeight } = currentMount;
            state.camera.aspect = clientWidth / clientHeight;
            state.camera.updateProjectionMatrix();
            state.renderer.setSize(clientWidth, clientHeight);
            state.labelRenderer!.setSize(clientWidth, clientHeight);
        };
        window.addEventListener('resize', handleResize);

        const handleCanvasClick = (event) => {
            // Null checks are important since we initialized with null
            if (!state.mouse || !state.raycaster || !state.camera) return;

            const rect = currentMount.getBoundingClientRect();
            state.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            state.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

            state.raycaster.setFromCamera(state.mouse, state.camera);
            const intersects = state.raycaster.intersectObjects(state.clickableBoxes, true);

            if (intersects.length > 0) {
                const clickedBox = intersects[0].object as THREE.Mesh;

                const retrievedSensorId = clickedBox.userData.sensorId;
                if (retrievedSensorId !== undefined) {

                    onSensorClick(retrievedSensorId);

                }
                clickedBox.material.color.set(0x00ff00);
                clickedBox.material.needsUpdate = true;

            }
        };
        currentMount.addEventListener('click', handleCanvasClick);


        // 6. Cleanup Function
        return () => {
    window.removeEventListener('resize', handleResize);
    currentMount.removeEventListener('click', handleCanvasClick);
    cancelAnimationFrame(state.frameId);

    // 1. Traverse and dispose of all scene objects
    if (state.scene) {
        state.scene.traverse((object) => {
            if (object instanceof THREE.Mesh) {
                object.geometry.dispose();

                if (Array.isArray(object.material)) {
                    object.material.forEach(mat => mat.dispose());
                } else {
                    object.material.dispose();
                }
            }
        });
        // Clear the scene
        while(state.scene.children.length > 0){
            state.scene.remove(state.scene.children[0]);
        }
    }

    // 2. Dispose of Renderers
    if (state.renderer) {
        state.renderer.dispose();
        if (currentMount.contains(state.renderer.domElement)) {
            currentMount.removeChild(state.renderer.domElement);
        }
    }

    if (state.labelRenderer && currentMount.contains(state.labelRenderer.domElement)) {
        currentMount.removeChild(state.labelRenderer.domElement);
    }

    // 3. Clear the state maps
    state.meshByName.clear();
    state.clickableBoxes = [];
    state.warningMeshes.clear();
    state.alarmMeshes.clear();
        };
    }, [device?.id, positionMap, configData?.silo_type?.model_url]); // Empty dependency array: runs only on mount/unmount


    return (
        <div style={{ padding: '20px' }}>
            <div
                ref={mountRef}
                style={{ width: "100%", height: "800px", border: "1px solid #ccc", position: 'relative' }}
            />
        </div>
    );
};

export default SiloModelCanvas;